import React, { useCallback, useEffect, useState } from "react";
import api from "../../util/panelApi";
import { PageHead, Tiles, Table, Tabs, Chip, Person, fmt, when } from "./kit";

const KINDS = [
  ["voice", "Sesli oda"],
  ["live", "Canlı yayın"],
  ["call", "Görüntülü arama"],
];
const KIND_LABEL = Object.fromEntries(KINDS);
const KIND_INFO = {
  voice: "Sesli oda: odada alınan hediyeler sayılır. Süre = koltukta, mikrofonu yönetici tarafından kapatılmamış süre.",
  live: "Canlı yayın: yayın hediyeleri, PK, profil hediyesi, fan kulübü ve koruma sayılır. Süre = canlı yayında geçen süre.",
  call: "Görüntülü arama: dakika ücretinden yayıncıya yazılan coin sayılır. Süre = görüşme süresi. Kapalıyken arama kazancı eski düzende kalır (dakika ücretinin %60'ı coin, anında).",
};
const usd = (n) => `${(Number(n) || 0).toLocaleString("tr-TR", { maximumFractionDigits: 2 })}$`;
const thisMonth = () => new Date(Date.now() + 3 * 3600 * 1000).toISOString().slice(0, 7);
const small = { fontSize: 12, color: "#6B7280" };
const cell = { width: "100%", minWidth: 70, padding: "4px 6px", border: "1px solid #E5E7EB", borderRadius: 6, fontSize: 13 };

const SalaryPolicy = () => {
  const [tab, setTab] = useState("policy");
  const [enabled, setEnabled] = useState(false);
  const [policy, setPolicy] = useState(null);
  const [kind, setKind] = useState("voice");
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");

  const [month, setMonth] = useState(thisMonth());
  const [report, setReport] = useState({ rows: [], totals: { coins: 0, hostUsd: 0, agencyUsd: 0 } });
  const [wStatus, setWStatus] = useState("pending");
  const [withdraws, setWithdraws] = useState([]);
  const [loading, setLoading] = useState(false);

  const flash = (m) => {
    setMsg(m);
    setTimeout(() => setMsg(""), 4000);
  };

  const loadPolicy = useCallback(async () => {
    const r = await api.salaryPolicy();
    if (r?.status) {
      setEnabled(r.enabled);
      setPolicy(r.policy);
    }
  }, []);

  const loadReport = useCallback(async () => {
    setLoading(true);
    const r = await api.salaryMonths({ month, kind });
    if (r?.status) setReport(r);
    setLoading(false);
  }, [month, kind]);

  const loadWithdraws = useCallback(async () => {
    setLoading(true);
    const r = await api.salaryWithdraws({ status: wStatus });
    if (r?.status) setWithdraws(r.withdraws);
    setLoading(false);
  }, [wStatus]);

  useEffect(() => {
    loadPolicy();
  }, [loadPolicy]);

  useEffect(() => {
    if (tab === "report") loadReport();
    if (tab === "withdraws") loadWithdraws();
  }, [tab, loadReport, loadWithdraws]);

  const toggle = async () => {
    const next = !enabled;
    const text = next
      ? "Maaş politikası açılsın mı? Açık olan tablonun (sesli/görüntülü) hediyeleri maaşa sayılır; kapalı tablonun kazancı eskisi gibi %60 coin olarak yayıncıya yazılır."
      : "Maaş politikası kapatılsın mı? Tablo uygulamadan kalkar, sayaçlar durur.";
    if (!window.confirm(text)) return;
    const r = await api.saveConfig({ salaryPolicyEnabled: next });
    if (!r?.status) return window.alert(r?.message || "Kaydedilemedi");
    setEnabled(next);
    flash(next ? "Açıldı" : "Kapatıldı");
  };

  const setLevel = (i, key, value) => {
    const levels = policy[kind].levels.map((row, j) => (j === i ? { ...row, [key]: value } : row));
    setPolicy({ ...policy, [kind]: { ...policy[kind], levels } });
  };
  const setRule = (key, value) => setPolicy({ ...policy, [kind]: { ...policy[kind], [key]: value } });
  // hedefsiz kademe (kind altinda daily nesnesi)
  const dailyOf = () => policy?.[kind]?.daily || { enabled: false, minutes: 240, usd: 0.1 };
  const setDaily = (key, value) => setRule("daily", { ...dailyOf(), [key]: value });
  // yeni yayinci odulu (ortak)
  const nhOf = () => policy?.newHost || { enabled: false, days: 21, usdPerDay: 0.6 };
  const setNh = (key, value) => setPolicy({ ...policy, newHost: { ...nhOf(), [key]: value } });
  const addRow = () => {
    const levels = policy[kind].levels;
    const last = levels[levels.length - 1] || { t: 0, h: 0, a: 0, hb: 0, ab: 0, sb: 0, yb: 0 };
    setPolicy({ ...policy, [kind]: { ...policy[kind], levels: [...levels, { ...last, t: Math.round(last.t * 1.2) || 50000 }] } });
  };
  const removeRow = () => {
    const levels = policy[kind].levels;
    if (levels.length <= 1) return;
    setPolicy({ ...policy, [kind]: { ...policy[kind], levels: levels.slice(0, -1) } });
  };

  const save = async () => {
    setSaving(true);
    const r = await api.salarySavePolicy(policy);
    setSaving(false);
    if (!r?.status) return window.alert(r?.message || "Kaydedilemedi");
    setPolicy(r.policy);
    flash("Kaydedildi");
  };

  const act = async (w, action) => {
    const note = action === "rejected" ? window.prompt("Ret sebebi (kullanıcıya iletilmez):", "") : "";
    if (note === null) return;
    if (action === "paid" && !window.confirm(`${w.name} için ${usd(w.usd)} ödendi olarak işaretlensin mi?`)) return;
    const r = await api.salaryWithdrawAction(w._id, { action, note });
    if (!r?.status) return window.alert(r?.message || "İşlem başarısız");
    flash(r.message);
    loadWithdraws();
  };

  const conf = policy?.[kind];

  return (
    <>
      <PageHead
        title="Maaş Politikası"
        sub="Yayıncı ve ajans maaş tablosu. Hedef = ay içinde alınan hediye coin'i, maaşlar dolar."
        right={
          <div className="d-flex align-items-center" style={{ gap: 10 }}>
            {msg && <span style={{ fontSize: 13, color: "#059669" }}>{msg}</span>}
            {enabled ? <Chip>Uygulamada açık</Chip> : <Chip kind="wait">Kapalı</Chip>}
            <button className={`btn ${enabled ? "btn-outline-danger" : "btn-success"}`} onClick={toggle}>
              {enabled ? "Kapat" : "Aç"}
            </button>
          </div>
        }
      />

      <Tabs
        tabs={[
          ["policy", "Tablo"],
          ["report", "Bu ay"],
          ["withdraws", "Maaş çekimleri"],
        ]}
        value={tab}
        onChange={setTab}
      />

      {tab !== "withdraws" && (
        <div className="d-flex align-items-center mb-3" style={{ gap: 8 }}>
          {KINDS.map(([k, label]) => (
            <button key={k} className={`btn btn-sm ${kind === k ? "btn-primary" : "btn-outline-secondary"}`} onClick={() => setKind(k)}>
              {label}
            </button>
          ))}
          {tab === "report" && <input type="month" className="form-control ml-2" style={{ width: 170 }} value={month} onChange={(e) => setMonth(e.target.value)} />}
        </div>
      )}

      {tab === "policy" && conf && (
        <>
          <div className="card mb-3">
            <div className="card-body d-flex flex-wrap" style={{ gap: 16 }}>
              <div>
                <div style={small}>{KIND_LABEL[kind]} tablosu</div>
                <button
                  className={`btn ${conf.enabled === true ? "btn-success" : "btn-outline-secondary"}`}
                  onClick={() => setRule("enabled", conf.enabled !== true)}
                >
                  {conf.enabled === true ? "Açık — kazanç maaşa sayılır" : "Kapalı — kazanç eskisi gibi %60 coin"}
                </button>
              </div>
              <div>
                <div style={small}>Geçerli gün için günlük süre (dakika)</div>
                <input className="form-control" type="number" value={conf.dayMinutes} onChange={(e) => setRule("dayMinutes", e.target.value)} />
              </div>
              <div>
                <div style={small}>Bonus için geçerli gün</div>
                <input className="form-control" type="number" value={conf.bonusDays} onChange={(e) => setRule("bonusDays", e.target.value)} />
              </div>
              <div>
                <div style={small}>Bonus için toplam saat</div>
                <input className="form-control" type="number" value={conf.bonusHours} onChange={(e) => setRule("bonusHours", e.target.value)} />
              </div>
              <div>
                <div style={small}>Çekim bekleme süresi (gün, ikisi için ortak)</div>
                <input className="form-control" type="number" value={policy.holdDays} onChange={(e) => setPolicy({ ...policy, holdDays: e.target.value })} />
              </div>
              <div>
                <div style={small}>En az çekim ($, ortak)</div>
                <input className="form-control" type="number" value={policy.minWithdrawUsd} onChange={(e) => setPolicy({ ...policy, minWithdrawUsd: e.target.value })} />
              </div>
            </div>
            <div className="card-body pt-0" style={{ fontSize: 13, color: "#374151" }}>
              {KIND_INFO[kind]}{" "}
              Ajans maaşı yayıncıdan kesilmez, şirket ayrıca öder.
            </div>
          </div>

          {/* Hedefsiz kademe + yeni yayinci odulu: izleyicisi olmayan yayinci da
              ilk gunden para gorsun diye. Hediye sarti yok, yalnizca yayin suresi. */}
          <div className="card mb-3">
            <div className="card-body">
              <div style={{ fontWeight: 700, marginBottom: 10 }}>Hedefsiz kademe — yeni yayıncıyı tutmak için</div>
              <div className="d-flex flex-wrap align-items-end" style={{ gap: 16 }}>
                <div>
                  <div style={small}>{KIND_LABEL[kind]} — durum</div>
                  <button
                    className={`btn ${dailyOf().enabled ? "btn-success" : "btn-outline-secondary"}`}
                    onClick={() => setDaily("enabled", !dailyOf().enabled)}
                  >
                    {dailyOf().enabled ? "Açık" : "Kapalı"}
                  </button>
                </div>
                <div>
                  <div style={small}>Günlük yayın süresi (dakika)</div>
                  <input className="form-control" type="number" value={dailyOf().minutes} onChange={(e) => setDaily("minutes", e.target.value)} />
                </div>
                <div>
                  <div style={small}>Günlük ödeme ($)</div>
                  <input className="form-control" type="number" step="0.01" value={dailyOf().usd} onChange={(e) => setDaily("usd", e.target.value)} />
                </div>
                <div style={{ borderLeft: "1px solid #E5E7EB", paddingLeft: 16 }}>
                  <div style={small}>Yeni yayıncı ödülü (ortak)</div>
                  <button
                    className={`btn ${nhOf().enabled ? "btn-success" : "btn-outline-secondary"}`}
                    onClick={() => setNh("enabled", !nhOf().enabled)}
                  >
                    {nhOf().enabled ? "Açık" : "Kapalı"}
                  </button>
                </div>
                <div>
                  <div style={small}>İlk kaç gün</div>
                  <input className="form-control" type="number" value={nhOf().days} onChange={(e) => setNh("days", e.target.value)} />
                </div>
                <div>
                  <div style={small}>Günlük ek ödeme ($)</div>
                  <input className="form-control" type="number" step="0.01" value={nhOf().usdPerDay} onChange={(e) => setNh("usdPerDay", e.target.value)} />
                </div>
              </div>
              <div style={{ fontSize: 13, color: "#374151", marginTop: 10 }}>
                Hediye şartı <b>yok</b> — yayıncı sadece süreyi doldurarak kazanır. Yayıncı başına <b>günde bir kez</b> ödenir
                (sesli ve görüntülü aynı gün yayın yapsa bile tek ödeme). Yeni yayıncı ilk {nhOf().days} gün ikisini birden alır:
                günlük <b>${((Number(dailyOf().usd) || 0) + (Number(nhOf().usdPerDay) || 0)).toFixed(2)}</b>, toplamda{" "}
                <b>${(((Number(dailyOf().usd) || 0) + (Number(nhOf().usdPerDay) || 0)) * (Number(nhOf().days) || 0)).toFixed(2)}</b>.
                Sonrasında günlük ${(Number(dailyOf().usd) || 0).toFixed(2)}.
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-body" style={{ overflowX: "auto" }}>
              <table className="table table-sm mb-2" style={{ minWidth: 960 }}>
                <thead>
                  <tr style={{ fontSize: 12, color: "#6B7280" }}>
                    <th>Seviye</th>
                    <th>Hedef (coin)</th>
                    <th>Yayıncı maaşı $</th>
                    <th>Ajans maaşı $</th>
                    <th>Yayıncı bonus $</th>
                    <th>Ajans bonus $</th>
                    <th title="Önceki dönemde de en az bu seviyeye ulaşıldıysa (gün/saat şartıyla)">Seviye bonusu $</th>
                    <th title="Önceki dönemin seviyesinin üstüne çıkıldıysa (gün/saat şartıyla)">Yükseltme bonusu $</th>
                    <th>Yayıncı en fazla</th>
                  </tr>
                </thead>
                <tbody>
                  {conf.levels.map((r, i) => (
                    <tr key={i}>
                      <td style={{ verticalAlign: "middle" }}>
                        <b>{i + 1}</b>
                      </td>
                      {["t", "h", "a", "hb", "ab", "sb", "yb"].map((k) => (
                        <td key={k}>
                          <input style={cell} type="number" value={r[k]} onChange={(e) => setLevel(i, k, e.target.value)} />
                        </td>
                      ))}
                      <td style={{ verticalAlign: "middle" }}>{usd(Number(r.h) + Number(r.hb) + (Number(r.sb) || 0) + (Number(r.yb) || 0))}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="d-flex align-items-center" style={{ gap: 8 }}>
                <button className="btn btn-sm btn-outline-secondary" onClick={addRow}>
                  Seviye ekle
                </button>
                <button className="btn btn-sm btn-outline-secondary" onClick={removeRow}>
                  Son seviyeyi sil
                </button>
                <button className="btn btn-primary ml-auto" disabled={saving} onClick={save}>
                  {saving ? "Kaydediliyor…" : "Tabloyu kaydet"}
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {tab === "report" && (
        <>
          <Tiles
            items={[
              { label: "Yayıncı", value: fmt(report.rows.length) },
              { label: "Toplam hediye coin", value: fmt(report.totals.coins) },
              { label: "Ödenen yayıncı maaşı", value: usd(report.totals.hostUsd) },
              { label: "Ödenen ajans maaşı", value: usd(report.totals.agencyUsd) },
            ]}
          />
          <Table
            loading={loading}
            empty="Bu ay için kayıt yok."
            head={["Yayıncı", "ID", "Coin", "Seviye", "Geçerli gün", "Süre", "Bonus", "Yayıncı maaşı", "Ajans maaşı"]}
            rows={report.rows.map((r) => (
              <tr key={r._id}>
                <td>
                  <Person image={r.image} name={r.name} />
                </td>
                <td>
                  <code>{r.uniqueID}</code>
                </td>
                <td>{fmt(r.coins)}</td>
                <td>
                  <b>{r.level}</b>
                </td>
                <td>{r.validDays}</td>
                <td>{Math.floor(r.minutes / 60)} sa</td>
                <td>{r.bonusLevel > 0 ? <Chip>Aldı</Chip> : <Chip kind="wait">Yok</Chip>}</td>
                <td>{usd(r.hostUsd)}</td>
                <td>{r.agencyId ? usd(r.agencyUsd) : "—"}</td>
              </tr>
            ))}
          />
        </>
      )}

      {tab === "withdraws" && (
        <>
          <div className="d-flex mb-3" style={{ gap: 8 }}>
            {[
              ["pending", "Bekleyen"],
              ["paid", "Ödenen"],
              ["rejected", "Reddedilen"],
            ].map(([s, label]) => (
              <button key={s} className={`btn btn-sm ${wStatus === s ? "btn-primary" : "btn-outline-secondary"}`} onClick={() => setWStatus(s)}>
                {label}
              </button>
            ))}
          </div>
          <Table
            loading={loading}
            empty="Talep yok."
            head={["Kişi", "ID", "Tutar", "Yöntem", "Hesap bilgisi", "Tarih", ""]}
            rows={withdraws.map((w) => (
              <tr key={w._id}>
                <td>{w.name}</td>
                <td>
                  <code>{w.uniqueID}</code>
                </td>
                <td>
                  <b>{usd(w.usd)}</b>
                </td>
                <td>{w.method}</td>
                <td style={{ maxWidth: 260, wordBreak: "break-word" }}>{w.details}</td>
                <td>{when(w.createdAt)}</td>
                <td style={{ whiteSpace: "nowrap" }}>
                  {w.status === "pending" ? (
                    <>
                      <button className="btn btn-sm btn-success mr-1" onClick={() => act(w, "paid")}>
                        Ödendi
                      </button>
                      <button className="btn btn-sm btn-outline-danger" onClick={() => act(w, "rejected")}>
                        Reddet
                      </button>
                    </>
                  ) : (
                    w.adminNote
                  )}
                </td>
              </tr>
            ))}
          />
        </>
      )}
    </>
  );
};

export default SalaryPolicy;
